import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data", "loading", "refetch", "fetchMore", "error"];
import { registerQuery, useQuery } from 'data-fetching-client';
import { useCallback, useMemo } from 'react';
import { mergeById } from '../utils/mergeById';
import { getIsPreview } from '../utils/isPreview';
import { getPreviewTicketThread } from './previewMocks';
import { useIsPreview } from '../hooks/useIsPreview';
import { chirpClient } from '../utils/chirpClient';
import { getThreadViewRpc } from '../../__generated__/chirp/com/hubspot/support/portal/rpc/ThreadViewRpc';
import { getPortalAccountId, getPortalInstanceId } from 'portals-platform-lib';
export const GET_TICKET_THREAD_KEY = 'getTicketThread';
const getTicketThread = async variables => {
  const {
    isPreview
  } = getIsPreview();
  if (isPreview) {
    return getPreviewTicketThread();
  }
  const {
    ticketId,
    threadId,
    offset
  } = variables;
  const response = await chirpClient.call(getThreadViewRpc, {
    request: {
      accountId: getPortalAccountId(),
      portalInstanceId: getPortalInstanceId(),
      params: Object.assign({
        ticketId,
        threadId
      }, offset != null ? {
        offsetTimestamp: offset
      } : {})
    }
  });
  const threadView = response.data;
  if (!threadView) {
    return {
      responders: {},
      attachments: {},
      sensitiveAttachmentsMetaData: {},
      recipients: undefined,
      results: [],
      hasMore: false,
      offset: {
        timeStamp: 0,
        ordinal: 0
      }
    };
  }
  const responders = {};
  (threadView.responders || []).forEach(({
    sender,
    threadViewResponder
  }) => {
    responders[JSON.stringify(sender)] = threadViewResponder;
  });
  return {
    responders,
    attachments: threadView.attachments,
    sensitiveAttachmentsMetaData: threadView.sensitiveAttachmentsMetaData,
    recipients: threadView.recipients,
    results: threadView.results || [],
    hasMore: threadView.hasMore,
    offset: threadView.offset
  };
};
export const GET_TICKET_THREAD_QUERY_POLICY = {
  [GET_TICKET_THREAD_KEY]: {
    keyArgs: ['ticketId', 'threadId'],
    merge: (existing, incoming) => {
      var _incoming$recipients;
      if (!existing) {
        return incoming;
      }
      const mergedResults = mergeById(existing.results || [], incoming.results || []);
      const allResultsLoaded = existing.hasMore === false;
      return {
        attachments: Object.assign({}, existing.attachments, incoming.attachments),
        responders: Object.assign({}, existing.responders, incoming.responders),
        recipients: (_incoming$recipients = incoming.recipients) !== null && _incoming$recipients !== void 0 ? _incoming$recipients : existing.recipients,
        results: mergedResults,
        hasMore: allResultsLoaded ? false : incoming.hasMore,
        offset: allResultsLoaded ? existing.offset : incoming.offset,
        sensitiveAttachmentsMetaData: Object.assign({}, existing.sensitiveAttachmentsMetaData, incoming.sensitiveAttachmentsMetaData)
      };
    }
  }
};
export const GET_TICKET_THREAD = registerQuery({
  fieldName: GET_TICKET_THREAD_KEY,
  args: ['ticketId', 'threadId', 'offset'],
  fetcher: getTicketThread
});
export const useTicketThread = (ticketId, threadId) => {
  const isPreview = useIsPreview();
  const _useQuery = useQuery(GET_TICKET_THREAD, {
      variables: {
        ticketId,
        threadId: threadId
      },
      skip: !ticketId || threadId === undefined,
      pollInterval: isPreview ? undefined : 5000,
      skipPollAttempt: () => document.visibilityState === 'hidden'
    }),
    {
      data,
      loading,
      refetch,
      fetchMore: _fetchMore,
      error
    } = _useQuery,
    rest = _objectWithoutPropertiesLoose(_useQuery, _excluded);
  const threadData = data === null || data === void 0 ? void 0 : data[GET_TICKET_THREAD_KEY];
  const hasMore = useMemo(() => {
    var _threadData$hasMore;
    return (_threadData$hasMore = threadData === null || threadData === void 0 ? void 0 : threadData.hasMore) !== null && _threadData$hasMore !== void 0 ? _threadData$hasMore : false;
  }, [threadData]);
  const offset = useMemo(() => {
    var _threadData$offset;
    return threadData === null || threadData === void 0 || (_threadData$offset = threadData.offset) === null || _threadData$offset === void 0 ? void 0 : _threadData$offset.timeStamp;
  }, [threadData]);
  const fetchMore = useCallback(() => {
    if (!hasMore) {
      return;
    }
    void _fetchMore({
      variables: {
        offset
      }
    });
  }, [_fetchMore, hasMore, offset]);
  const lastMessageId = useMemo(() => {
    var _threadData$results;
    return threadData === null || threadData === void 0 || (_threadData$results = threadData.results) === null || _threadData$results === void 0 || (_threadData$results = _threadData$results.at(-1)) === null || _threadData$results === void 0 ? void 0 : _threadData$results.id;
  }, [threadData]);
  return Object.assign({
    data: threadData,
    hasMore,
    refetch,
    loading,
    fetchMore,
    lastMessageId,
    error
  }, rest);
};